<template>
  <div>
    <!-- Start Hero -->
    <!-- Hero image is the LCP candidate. Earlier the source was inlined as
         CSS background-image — that bypasses IPX (no webp) and Nuxt's preload
         pipeline, so the resource only starts after CSS parse. <NuxtImg> with
         preload + fetchpriority="high" + IPX webp halves mobile LCP per
         PSI 2026-05-04 (apartment avg LCP 7.6s on raw bg-image). -->
    <section
      class="relative flex min-h-[48vh] w-full items-center overflow-hidden pt-6 pb-20 sm:min-h-[52vh] sm:pt-10 sm:pb-28 md:pt-14 md:pb-32"
    >
      <!-- Plain <img> fed from the SAME getSizes() result as the rel=preload
           in useHead below — the two srcset strings are literally one computed
           value, so the preloaded URL always matches what the browser paints.
           <NuxtImg> is deliberately NOT used here: it serializes IPX modifiers
           in a different order (w_640&f_webp vs f_webp&w_640), producing
           different URLs than getSizes() — the preload missed and the hero
           downloaded twice (PSI 2026-06-10: 362ms resource load delay). -->
      <img
        :src="heroFallbackSrc"
        :srcset="heroPreload.srcset"
        :sizes="heroPreload.sizes"
        :alt="ogImageAlt"
        loading="eager"
        fetchpriority="high"
        class="absolute inset-0 h-full w-full object-cover object-center"
      >
      <div
        class="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-900/80 to-slate-900"
      ></div>
      <div class="container relative z-10">
        <div class="grid grid-cols-1 text-center">
          <h1
            class="mt-16 font-display text-3xl italic leading-[1.1] text-balance [overflow-wrap:anywhere] text-white drop-shadow sm:mt-24 sm:text-4xl md:mt-32 md:text-5xl lg:text-6xl"
            v-html="titleHtml"
          />
          <div class="mt-12 flex justify-center sm:mt-16 md:mt-20">
            <NuxtLink
              :to="heroSearchTo"
              class="inline-flex items-center gap-1.5 rounded-full bg-amber-400 px-5 py-2.5 font-body text-xs font-semibold uppercase tracking-[0.14em] text-slate-900 shadow-sm transition-colors hover:bg-amber-300 sm:gap-2 sm:px-7 sm:py-3 sm:text-sm sm:tracking-[0.18em]"
            >
              {{ $t('searchAllCta.heroAction') }}
              <svg
                class="h-3.5 w-3.5 sm:h-4 sm:w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </NuxtLink>
          </div>
        </div>
        <!--end grid-->
      </div>
      <!--end container-->
    </section>
    <!--end section-->
    <!-- End Hero -->

    <!-- Start -->
    <section class="section-y relative z-10 -mt-20 sm:-mt-28 md:-mt-36">
      <div class="container relative">
        <div class="grid md:grid-cols-12 grid-cols-1 gap-6">
          <!-- Start Main Post Area -->
          <div class="lg:col-span-9 md:col-span-8 md:row-start-1 order-1 md:order-none">
            <!-- Start Images Area -->
            <ApartmentImagesGrid
              :images="apartmentImages"
              :listing-title="apartment?.title || ''"
            />
            <!-- end Images Area -->

            <!-- Mobile inline CTA — anchor link to booking widget. Real
                 <a href="#..."> so search engines see the internal link;
                 click handler upgrades to smooth scroll + analytics. -->
            <a
              id="apartment-inline-cta"
              href="#booking-quote-widget"
              class="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-3 font-body text-sm font-semibold uppercase tracking-[0.12em] text-white shadow-sm transition-all hover:bg-red-600 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300 focus-visible:ring-offset-2 sm:hidden"
              @click="scrollToBookingQuote"
            >
              <Icon name="lucide:calendar-check" class="size-5 shrink-0" aria-hidden="true" />
              <span>{{ $t('stickyApartment.cta') }}</span>
            </a>

            <BookingAskQuestionCta
              v-if="apartment"
              class="mt-3 justify-center sm:hidden"
              placement="apartment_inline"
              :listing-id="Number(apartment.listing_id)"
              :title="apartment.title"
            />

            <h5 class="text-2xl md:text-3xl font-semibold mt-5">
              {{ apartment?.title }}
            </h5>
            <p class="flex items-center text-sm md:text-base text-slate-400 font-medium mt-2">
              {{ apartment?.hotel }}
            </p>

            <ul class="list-none grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-4 gap-y-3 md:gap-y-2">
              <li class="inline-flex items-center mt-5">
                <Icon
                  name="cuida-building-outline"
                  size="26px"
                  class="size-6 stroke-[1.5] text-red-500"
                />

                <div class="ms-3">
                  <p class="font-medium">{{ $t('apartment.facts.building') }}</p>
                  <span class="text-slate-400 font-medium text-sm md:text-base">{{
                    apartment?.building || $t('common.notAvailable')
                  }}</span>
                </div>
              </li>

              <li class="inline-flex items-center mt-5">
                <Icon
                  name="material-symbols:floor"
                  size="26px"
                  class="size-6 stroke-[1.5] text-red-500"
                />

                <div class="ms-3">
                  <p class="font-medium">{{ $t('apartment.facts.floor') }}</p>
                  <span class="text-slate-400 font-medium text-sm md:text-base">{{
                    apartment?.floor || $t('common.notAvailable')
                  }}</span>
                </div>
              </li>

              <li class="inline-flex items-center mt-5">
                <Icon
                  name="quill-activity"
                  size="26px"
                  class="size-6 stroke-[1.5] text-red-500"
                />

                <div class="ms-3">
                  <p class="font-medium">{{ $t('apartment.facts.rooms') }}</p>
                  <span class="text-slate-400 font-medium text-sm md:text-base">{{
                    apartment?.rooms || $t('common.notAvailable')
                  }}</span>
                </div>
              </li>

              <li class="inline-flex items-center mt-5">
                <Icon
                  name="tabler:users"
                  size="26px"
                  class="size-6 stroke-[1.5] text-red-500"
                />

                <div class="ms-3">
                  <p class="font-medium">{{ $t('apartment.facts.groupSize') }}</p>
                  <span class="inline-flex items-baseline gap-1 text-slate-400 font-medium text-sm md:text-base">
                    <span>{{ baseGuests || apartment?.capacity || $t('common.notAvailable') }}</span>
                    <span
                      v-if="extraGuests > 0"
                      :title="extraGuestTitle"
                      class="inline-flex items-center rounded bg-amber-100/70 px-1 font-semibold tabular-nums text-amber-700 cursor-help dark:bg-amber-400/15 dark:text-amber-300"
                    >+{{ extraGuests }}</span>
                  </span>
                </div>
              </li>

              <li class="inline-flex items-center mt-5">
                <Icon
                  name="icons8:price-tag"
                  size="26px"
                  class="size-6 stroke-[1.5] text-red-500"
                />

                <div class="ms-3">
                  <p class="font-medium">{{ $t('apartment.price_label') }}</p>
                  <span v-if="fromNightly" class="text-slate-400 font-medium text-sm md:text-base whitespace-nowrap">
                    {{ $t('apartment.from_nightly', { price: fromNightly }) }}
                  </span>
                </div>
              </li>
            </ul>

            <ApartmentFacts v-if="apartment" :apartment="apartment" />

            <!-- Description is rendered Lazy* so the markdown-render chunk
                 ships only when the user scrolls past the facts grid. Mobile
                 TBT win per PSI 2026-05-04 (apt avg TBT 1073 ms). -->
            <LazyApartmentDescription :doc="apartment" />

          </div>

          <div id="booking-quote-widget" class="scroll-mt-20 lg:col-span-3 md:col-span-4 md:row-start-1 md:row-span-2 order-2 md:order-none">
            <!-- hydrate-on-visible: the widget embeds @vuepic/vue-datepicker
                 (~381ms of client JS, PSI 2026-06-03 apartment TBT 440ms). On
                 mobile it sits below the gallery (order-2), so deferring its
                 hydration until it scrolls into view keeps the datepicker chunk
                 off the initial main thread — cuts TBT, frees LCP. SSR markup
                 (dates/guests/price) still renders; only hydration waits. The
                 datepicker itself is already <ClientOnly>, so nothing it shows
                 is lost. NOT applied to the hero — see feedback_async_component_in_hero. -->
            <LazyVisibleBookingQuoteWidget
              v-if="apartment"
              v-model:date-range="dateRange"
              v-model:guests="guests"
              hydrate-on-visible
              :listing-id="Number(apartment.listing_id)"
              :title="apartment.title"
              :capacity="Number(apartment.capacity)"
              :quote="quote"
              :quote-pending="quotePending"
              :quote-error="quoteError"
            />
          </div>

          <!-- Lazy: Request panel only renders when a valid quote exists,
               so its bundle is dead weight on first paint. -->
          <LazyBookingRequestPanel
            v-if="apartment && quote && quote.available && quote.nights >= MIN_BOOKING_REQUEST_NIGHTS && checkin && checkout"
            class="lg:col-span-9 md:col-span-8 md:col-start-1 md:row-start-2 order-3 md:order-none"
            :listing-id="Number(apartment.listing_id)"
            :apartment-title="apartment.title"
            :checkin="checkin"
            :checkout="checkout"
            :guests="guests"
            :quote="{
              totalUsd: quote.totalUsd,
              nights: quote.nights,
              tier: quote.tier,
              poolIncluded: quote.poolIncluded,
            }"
            :locale="(locale as 'en' | 'ru' | 'ka')"
          />
        </div>
      </div>
      <!--end container-->
    </section>
    <!--end section-->

    <!-- Below-fold "what else to know" links — wrapped in cv-auto so the
         browser skips paint until scroll, and Lazy* so the chunk only ships
         when reached. Bottom of page = always below fold. -->
    <div class="cv-auto">
      <LazyAboutFurtherBlock :block="furtherBlock" />
    </div>
    <StickyBookCta />
  </div>
</template>
<script setup lang="ts">
import {
  buildApartmentLd,
  buildBreadcrumbLd,
} from "~/utils/apartmentSchema";
import { useQuote } from "~/composables/useQuote";
import { toIsoLocal } from "~/utils/iso-date";
import { quoteState } from "~/utils/quoteState";
import { MIN_BOOKING_REQUEST_NIGHTS } from "~~/server/services/quote-engine";

const { t, locale } = useI18n();
const localePath = useLocalePath();
const route = useRoute();
const listing_id = route.params.id;

useReveal(
  import.meta.client && window.matchMedia('(max-width: 768px)').matches
    ? { threshold: 0, rootMargin: '0px 0px 280px 0px' }
    : undefined,
);

const { data: apartment } = await useAsyncData(
  `apartment-${listing_id}-${locale.value}`,
  async () => {
    const collection = ("apartments_" + locale.value) as "apartments_en";
    const content = await queryCollection(collection)
      .where("listing_id", "=", listing_id)
      .first();

    return content;
  }
);

// Without this, downstream code (computed `apartment.value?.listing_id`,
// schema builders) still ran on null and the SSR template threw a generic
// 500. Returning a real 404 keeps Google's index hygiene clean and stops
// soft-404 signals in Search Console.
if (!apartment.value) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Apartment not found',
    fatal: true,
  });
}

// Content markdown can exist for an apartment hidden from the public
// (active=true, listed=false) — staged for instant publish once the operator
// flips the search-visibility toggle. Until then the detail page must 404 so
// the listing stays unreachable and unindexable.
const { data: visibility } = await useFetch<{ visible: boolean }>(
  `/api/apartments/${listing_id}/visible`,
  { default: () => ({ visible: false }) },
);
if (!visibility.value?.visible) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Apartment not found',
    fatal: true,
  });
}

const { data: fromPrices } = await useFetch<Record<number, number>>('/api/apartments/from-prices', { default: () => ({}) });
const fromNightly = computed(() =>
  fromPrices.value[Number(apartment.value?.listing_id)] ?? null
);

type PriceRange = { lowPrice: number; highPrice: number; offerCount: number };
const { data: priceRanges } = await useFetch<Record<number, PriceRange>>(
  '/api/apartments/price-ranges',
  { default: () => ({}) },
);
const priceRange = computed<PriceRange | null>(() =>
  priceRanges.value[Number(apartment.value?.listing_id)] ?? null,
);

type SeasonOffer = { name: string; dateStart: string; dateEnd: string; dailyRateUsd: number };
const { data: seasonsMap } = await useFetch<Record<number, SeasonOffer[]>>(
  '/api/apartments/seasons',
  { default: () => ({}) },
);
const seasonOffers = computed<SeasonOffer[]>(() =>
  seasonsMap.value[Number(apartment.value?.listing_id)] ?? [],
);

type GuestFacts = { capacity: number; threshold: number; extraGuestUsd?: number };
const { data: guestFactsMap } = await useFetch<Record<number, GuestFacts>>(
  '/api/apartments/facts',
  { default: () => ({}) },
);
const guestFacts = computed<GuestFacts | null>(() => {
  const id = Number(apartment.value?.listing_id);
  return Number.isFinite(id) ? guestFactsMap.value[id] ?? null : null;
});

const { baseGuests, extraGuests, extraGuestUsd } = useApartmentFacts(
  () => ({
    capacity: apartment.value?.capacity ?? null,
    rooms: apartment.value?.rooms ?? null,
    floor: apartment.value?.floor ?? null,
    view_type: apartment.value?.view_type ?? null,
    area_sqm: apartment.value?.area_sqm ?? null,
  }),
  () => guestFacts.value,
);

const extraGuestTitle = computed(() =>
  extraGuestUsd.value
    ? t('apartmentRow.facts.extraGuestHintPriced', { price: extraGuestUsd.value })
    : t('apartmentRow.facts.extraGuestHint'),
);

// Booking state — owned here so QuoteWidget can v-model it and the hero
// "All apartments" CTA above reads checkin/checkout/guests directly.
//
// Initial values come from URL query. Primary names `?from=&to=` match what
// every internal link writes (ApartmentRow on /search, BookingSearchForm,
// hero CTA above). Legacy `?checkin=&checkout=` is kept as a fallback for
// IG/Story link-stickers that might use that convention. Without this, SSR
// renders an empty calendar even when the URL carries dates, causing a
// hydration flash before QuoteWidget.hydrateFromQuery catches up.
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function parseIsoDate(s: unknown): Date | null {
  if (typeof s !== 'string' || !ISO_DATE_RE.test(s)) return null;
  // Noon to be defensive against DST transitions on the picker.
  const d = new Date(`${s}T12:00:00`);
  return Number.isFinite(d.getTime()) ? d : null;
}
const initialCheckin =
  parseIsoDate(route.query.from) ?? parseIsoDate(route.query.checkin);
const initialCheckout =
  parseIsoDate(route.query.to) ?? parseIsoDate(route.query.checkout);
const initialGuestsParam = Number(route.query.guests);
const initialGuests =
  Number.isInteger(initialGuestsParam) && initialGuestsParam >= 1 && initialGuestsParam <= 32
    ? initialGuestsParam
    : 2;
const dateRange = ref<[Date, Date] | null>(
  initialCheckin && initialCheckout && initialCheckin < initialCheckout
    ? [initialCheckin, initialCheckout]
    : null,
);
const guests = ref(initialGuests);
const checkin = computed(() => (dateRange.value?.[0] ? toIsoLocal(dateRange.value[0]) : ''));
const checkout = computed(() => (dateRange.value?.[1] ? toIsoLocal(dateRange.value[1]) : ''));
const listingIdRef = computed<number | null>(() => {
  const id = Number(apartment.value?.listing_id);
  return Number.isFinite(id) && id > 0 ? id : null;
});

const { quote, pending: quotePending, error: quoteError } = useQuote({
  listingId: listingIdRef,
  checkin,
  checkout,
  guests,
});

// Hero "View all apartments" link — preserve dates+guests from the booking
// panel whenever the user has picked a full range. The CTA's main job is
// "find me others" when this apartment is taken, so we don't gate on
// availability — that's exactly when carrying the dates is most useful.
const heroSearchTo = computed(() =>
  checkin.value && checkout.value
    ? localePath({
        path: '/search',
        query: { from: checkin.value, to: checkout.value, guests: guests.value },
      })
    : localePath('/search'),
);

// Track apartment view as `ViewContent` for Meta Pixel + standard
// `apartment_view` for GA4/YM. Fires once per mount when listing data
// is available — useAsyncData hydrates synchronously on client-side
// nav, so onMounted is reliable here.
const { trackEvent } = useAnalytics();
onMounted(() => {
  const id = listingIdRef.value;
  if (!id || !apartment.value?.title) return;
  trackEvent('apartment_view', {
    listing_id: id,
    apartment_title: apartment.value.title,
    daily_rate_usd: fromNightly.value ?? null,
  });
});

// Funnel instrumentation: record how the quote settles per apartment view —
// available / unavailable (+reason) / no_dates — so the pre-booking drop-off can
// be sized by cause instead of guessed. Deduped per distinct state key; skips the
// debounce window where the quote is cleared but not yet refetched (useQuote sets
// `pending` only inside the fetch, see useQuote.ts), to avoid a false 'unavailable'.
const lastQuoteKey = ref('');
watch(
  [quote, quotePending, checkin, checkout],
  () => {
    const id = listingIdRef.value;
    if (!id || quotePending.value) return;
    const hasDates = !!checkin.value && !!checkout.value;
    if (hasDates && !quote.value) return; // in-flight (debounce) or fetch error — not settled
    const state = quoteState({
      available: quote.value?.available,
      hasCheckin: !!checkin.value,
      hasCheckout: !!checkout.value,
      nights: quote.value?.nights,
    });
    const reason = state === 'unavailable' ? (quote.value?.reason ?? null) : null;
    const key = `${id}:${state}:${reason ?? ''}`;
    if (key === lastQuoteKey.value) return;
    lastQuoteKey.value = key;
    trackEvent('quote_resolved', {
      listing_id: id,
      quote_state: state,
      quote_reason: reason,
      nights: quote.value?.nights ?? 0,
      total_usd: quote.value?.totalUsd ?? 0,
    });
  },
  { immediate: true },
);

function scrollToBookingQuote(event: MouseEvent) {
  const target = document.getElementById('booking-quote-widget');
  if (target) {
    event.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  trackEvent('booking_widget_click', {
    placement: 'apartment_inline',
    channel: 'anchor',
  });
}

const furtherBlock = computed(() => ({
  eyebrow: t('apartment.further.eyebrow'),
  header: t('apartment.further.header'),
  intro: t('apartment.further.intro'),
  items: [
    {
      title: t('apartment.further.items.guestServices.title'),
      description: t('apartment.further.items.guestServices.description'),
      href: '/guest-services',
      icon: 'headphones' as const,
    },
    {
      title: t('apartment.further.items.amenities.title'),
      description: t('apartment.further.items.amenities.description'),
      href: '/amenities',
      icon: 'sparkles' as const,
    },
    {
      title: t('apartment.further.items.location.title'),
      description: t('apartment.further.items.location.description'),
      href: '/location',
      icon: 'map-pin' as const,
    },
    {
      title: t('apartment.further.items.catalog.title'),
      description: t('apartment.further.items.catalog.description'),
      href: '/search',
      icon: 'key-round' as const,
    },
  ],
}));

// Content schema stores gallery entries as { url, alt } objects; the
// generated collection type is loose, hence the cast to the grid's shape.
const apartmentImages = computed<{ url?: string; src?: string; alt?: string }[]>(() =>
  Array.isArray(apartment.value?.photo_gallery)
    ? (apartment.value.photo_gallery as { url?: string; src?: string; alt?: string }[])
    : []
);

const HERO_FALLBACK = "/images/complex/2026-04-22-22-35-00-territory-warm-4x3.webp";
const heroImage = computed(() => {
  const cover = (apartment.value?.cover_photo as { url?: string }[] | undefined)?.[0]?.url;
  const firstPhoto = (apartment.value?.photo_gallery as { url?: string }[] | undefined)?.[0]?.url;
  return cover || firstPhoto || HERO_FALLBACK;
});

// Responsive LCP preload for the hero. The SAME getSizes() result feeds both
// the rel=preload link and the hero <img> srcset/sizes in the template, so
// the preloaded URL is guaranteed to be the one the browser paints. Do NOT
// switch the hero back to <NuxtImg>: its internal modifier serialization
// (w_640&f_webp&q_72) differs from getSizes() output (f_webp&q_72&w_640),
// the URLs stop matching and the image downloads twice.
const $img = useImage();
const HERO_SIZES = "xs:100vw sm:100vw md:100vw lg:1280px xl:1536px";
const heroPreload = computed(() =>
  $img.getSizes(heroImage.value, {
    sizes: HERO_SIZES,
    modifiers: { format: "webp", quality: 72 },
  })
);
// No-srcset fallback for legacy browsers; mid-tier width.
const heroFallbackSrc = computed(() =>
  $img(heroImage.value, { format: "webp", quality: 72, width: 1280 })
);
useHead(() => ({
  link: [
    {
      rel: "preload",
      as: "image",
      imagesrcset: heroPreload.value.srcset,
      imagesizes: heroPreload.value.sizes,
      fetchpriority: "high",
    },
  ],
}));

const titleHtml = computed(() => {
  const raw = apartment.value?.title || "";
  const escaped = raw
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return escaped.replace(/&amp;/g, '<span class="relative z-20 accent-amp">&amp;</span>');
});

const canonicalUrl = computed(
  () => `https://georgiadream.ge${route.path}`
);

// TEMPORARY (until 14F units are photographed). 141412/141413 have no own
// photos — their content md reuses the 13th-floor twins' gallery
// (/images/apartments/13{12,13}/), and hero copy is ~81% identical, so Google
// sees a thin near-duplicate pair. Point rel=canonical at the 13F twin to
// consolidate ranking signals onto one strong page instead of splitting them.
// The 14F pages stay live and bookable — canonical only affects organic
// dedup; schema @id / breadcrumb stay self-referential to avoid @id
// collisions. Mirrored in server/api/sitemap.ts (these IDs are excluded).
// REVERSE: delete this map + the sitemap exclusion once real 14F photos ship.
const CANONICAL_TWIN: Record<number, number> = { 141412: 141312, 141413: 141313 };

const canonicalHref = computed(() => {
  const twin = CANONICAL_TWIN[Number(apartment.value?.listing_id)];
  if (!twin) return canonicalUrl.value;
  return `https://georgiadream.ge${route.path.replace(
    /\/apartment\/\d+/,
    `/apartment/${twin}`
  )}`;
});

const ogImage = computed(() => {
  const cover = apartment.value?.cover_photo?.[0]?.url;
  const fallback = apartment.value?.photo_gallery?.[0]?.url;
  const path = cover || fallback || "/videos/DreamLand-full.jpg";
  return path.startsWith("http") ? path : `https://georgiadream.ge${path}`;
});

const ogImageAlt = computed(() =>
  apartment.value?.cover_photo?.[0]?.alt
  || apartment.value?.photo_gallery?.[0]?.alt
  || apartment.value?.title
);

const ogLocale = computed(() =>
  locale.value === "ru" ? "ru_RU" : locale.value === "ka" ? "ka_GE" : "en_US"
);
const ogLocaleAlternate = computed(() =>
  ["en_US", "ru_RU", "ka_GE"].filter((l) => l !== ogLocale.value)
);

useHead({
  link: [{ rel: "canonical", href: canonicalHref }],
  meta: [
    { name: "keywords", content: apartment.value?.keywords?.join(", ") },
  ],
});

const seoTitle = () => apartment.value?.seo_title || apartment.value?.title;
const seoDescription = () =>
  apartment.value?.meta_description || apartment.value?.description;

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogType: "website",
  ogUrl: canonicalUrl,
  ogImage,
  ogImageAlt,
  ogSiteName: "Dreamland Oasis, Batumi",
  ogLocale,
  ogLocaleAlternate,
  twitterCard: "summary_large_image",
  twitterTitle: seoTitle,
  twitterDescription: seoDescription,
  twitterImage: ogImage,
  twitterImageAlt: ogImageAlt,
});

const apartmentJsonLd = computed(() => {
  const a = apartment.value;
  if (!a) return "";
  return JSON.stringify(
    buildApartmentLd({
      apartment: a as Parameters<typeof buildApartmentLd>[0]["apartment"],
      canonicalUrl: canonicalUrl.value,
      locale: locale.value,
      fromNightly: fromNightly.value,
      priceRange: priceRange.value,
      seasons: seasonOffers.value,
    })
  );
});

const breadcrumbJsonLd = computed(() => {
  const a = apartment.value;
  if (!a) return "";
  const homeUrl = `https://georgiadream.ge${localePath("index")}`;
  return JSON.stringify(
    buildBreadcrumbLd({
      listingName: a.title,
      canonicalUrl: canonicalUrl.value,
      homeUrl,
      labels: {
        home: t("nav.home"),
      },
    })
  );
});

useHead({
  script: [
    {
      type: "application/ld+json",
      innerHTML: apartmentJsonLd,
    },
    {
      type: "application/ld+json",
      innerHTML: breadcrumbJsonLd,
    },
  ],
});
</script>
